import { getAccessToken, refreshTokens } from "./authTokens";

// Tiny fetch wrapper preserving superagent's API shape so 17 callsites can
// keep `requestWithAuth("post", url).send(body)` and read `.body` / `.status`
// from the resolved response. Replaces the 167KB superagent dependency.

type Method = "get" | "post" | "put" | "delete" | "patch";

export type RequestResponse<T = unknown> = {
	body: T;
	text: string;
	status: number;
	ok: boolean;
};

// Superagent throws on 4xx/5xx with an `error.response.{body,status}` shape;
// existing catch-blocks (e.g. `err?.response?.body?.error`) rely on that.
class HttpError extends Error {
	response: RequestResponse;
	status: number;
	constructor(response: RequestResponse) {
		super(`HTTP ${response.status}`);
		this.response = response;
		this.status = response.status;
	}
}

// Thenable mirroring superagent's chainable request. .send() sets the JSON body;
// awaiting the request (or chaining .then) triggers the fetch. Implements the
// full Promise interface so callers can chain .catch / .finally and use it
// anywhere a Promise is expected (e.g. react-query's MutationFunction).
class Request<T = unknown> implements Promise<RequestResponse<T>> {
	readonly [Symbol.toStringTag] = "Promise";

	private method: Method;
	private url: string;
	private body: unknown = undefined;
	private cached: Promise<RequestResponse<T>> | null = null;

	constructor(method: Method, url: string) {
		this.method = method;
		this.url = url;
	}

	send(body: unknown): this {
		this.body = body;
		return this;
	}

	private async dispatch(): Promise<Response> {
		const headers: Record<string, string> = {};
		const token = getAccessToken();
		if (token) headers.Authorization = `Bearer ${token}`;
		if (this.body !== undefined) headers["Content-Type"] = "application/json";

		return fetch(this.url, {
			method: this.method.toUpperCase(),
			headers,
			body: this.body !== undefined ? JSON.stringify(this.body) : undefined,
		});
	}

	private async execute(): Promise<RequestResponse<T>> {
		let res = await this.dispatch();

		// The access token lasts 15 minutes, so an expired one is routine rather
		// than exceptional. Refresh once and replay; if the refresh also fails the
		// 401 stands and authTokens has already signalled the sign-out.
		if (res.status === 401) {
			const refreshed = await refreshTokens();
			if (refreshed) {
				res = await this.dispatch();
			}
		}

		// Parse body: try JSON first (matches superagent's default), fall back
		// to text. Empty bodies become undefined like superagent does.
		const text = await res.text();
		let parsed: unknown;
		if (text) {
			try {
				parsed = JSON.parse(text);
			} catch {
				parsed = text;
			}
		}

		const response: RequestResponse<T> = {
			body: parsed as T,
			text,
			status: res.status,
			ok: res.ok,
		};

		if (!res.ok) throw new HttpError(response);
		return response;
	}

	private get promise(): Promise<RequestResponse<T>> {
		if (!this.cached) this.cached = this.execute();
		return this.cached;
	}

	// biome-ignore lint/suspicious/noThenProperty: implementing PromiseLike requires a `then` method.
	then<TResult1 = RequestResponse<T>, TResult2 = never>(
		onfulfilled?:
			| ((value: RequestResponse<T>) => TResult1 | PromiseLike<TResult1>)
			| null,
		onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
	): Promise<TResult1 | TResult2> {
		return this.promise.then(onfulfilled, onrejected);
	}

	catch<TResult = never>(
		onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null,
	): Promise<RequestResponse<T> | TResult> {
		return this.promise.catch(onrejected);
	}

	finally(onfinally?: (() => void) | null): Promise<RequestResponse<T>> {
		return this.promise.finally(onfinally);
	}
}

const requestWithAuth = <T = unknown>(
	method: Method,
	url: string,
): Request<T> => new Request<T>(method, url);

/**
 * The server's own error message, which `Error.message` does not carry — it
 * arrives in the response body and is the only text worth showing a user.
 * Returns undefined for network failures and anything else without a body.
 *
 * Lives here rather than being rewritten inline at each call site — it was
 * copied into four files — and being a named call also keeps the optional
 * chaining out of the `try`/`catch` blocks it is used in, which React Compiler
 * will not compile past.
 */
export function serverError(error: unknown): string | undefined {
	return (error as { response?: { body?: { error?: string } } })?.response?.body
		?.error;
}

export default requestWithAuth;
